import { Fragment, FC, memo, useCallback, useEffect, useRef } from 'react';
import useToggle from 'react-use/lib/useToggle';
import { AspectRatio, Box, Flex, IconButton, Progress, SxStyleProp } from 'theme-ui';
import { Theme } from 'theme';
import { ScalingType, Scene, SceneType } from 'types/Scene';
import { centeredContent, fullSize } from 'utils/mixins';
import { withStopPropagation } from 'utils/events';
import Icon, { IconTypes } from 'shared/Icon';
import { TrimModalProps } from './sceneCard/types';
import ImageDurationModal from './sceneCard/ImageDurationModal';
import { getTime } from 'utils/formatTime';
import { ScalingModeSelect } from './sceneCard/ScalingModeSelect';
import VideoTrimModal from './sceneCard/VideoTrimModal';
import { SetScenesDownloadingProgressAction } from 'modules/sceneDownloadingProgress';

import Tooltip from '@picsart/rc/old/Tooltip';

import { useTranslator } from '@picsart/rc/services/localization';
import analytics from 'utils/analytics';
import { ToolName } from 'types/AppState';

export type SceneCardProps = {
  active: boolean;
  selectHandler: () => void;
  removeHandler: () => void;
  scaleModeChangeHandler: (scalingMode: ScalingType) => void;
  trimHandler: TrimModalProps['applyHandler'];
  creationProgress?: number;
  mute: boolean;
  setMute: (isMute: boolean) => void;
  setScenesDownloadingProgressHandler: SetScenesDownloadingProgressAction;
  downloadingProgress: number;
  isScenePaid: boolean;
  setPlayingHandler: (isPlaying: boolean) => void;
  isUploaded: Scene['uploaded'];
  localSource: string;
} & Scene;

const previewStyles: SxStyleProp = {
  ...fullSize,
  bg: 'text',
  pointerEvents: 'none'
};

const emptySceneStyles: SxStyleProp = {
  ...fullSize,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background .2s ease-out',
  bg: 'grey1'
};
const emptyActiveSceneStyles: SxStyleProp = {
  ...emptySceneStyles,
  cursor: 'default'
};

const iconStyles: SxStyleProp = {
  '&:hover svg, &:focus svg': { variant: 'primary' }
};

const headerStyles: SxStyleProp = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  px: '12px',
  height: 40,
  zIndex: 'sceneCardHeader'
};

const selectHandleStyle: SxStyleProp = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  cursor: 'pointer'
};

const Icons = ({ iconType, isScenePaid }: { iconType: string; isScenePaid: boolean }) => {
  const translator = useTranslator();
  return (
    <Flex sx={{ alignItems: 'center', position: 'absolute', left: 4, bottom: 4 }}>
      {isScenePaid && (
        <Box data-test="scene_crown" sx={{ position: 'relative' }}>
          <Tooltip content={translator('This_scene_contains_premium_content')}>
            <Icon name="gold-circle-gen" sx={{ mr: 1, cursor: 'pointer', display: 'block' }} />
          </Tooltip>
        </Box>
      )}
      <Icon
        name={iconType}
        type={IconTypes.White}
        sx={{ backgroundColor: 'grey6', borderRadius: 4 }}
      />
    </Flex>
  );
};

const Duration = ({ value }: { value: number }) => (
  <Box
    sx={{
      position: 'absolute',
      right: 4,
      bottom: 4,
      bg: 'grey6',
      px: 3,
      py: 1,
      fontSize: 1,
      fontWeight: 'heading',
      color: 'white',
      borderRadius: 11
    }}
  >
    {getTime(value)}
  </Box>
);

const Header = memo(
  ({
    empty,
    removeHandler,
    loading,
    scaleModeChangeHandler,
    scalingMode,
    muteHandler,
    type,
    mute,
    setMute,
    trimHandler,
    toggleTrimModalOpened
  }: Pick<Scene, 'type'> &
    Pick<
      SceneCardProps,
      | 'removeHandler'
      | 'scaleModeChangeHandler'
      | 'scalingMode'
      | 'mute'
      | 'setMute'
      | 'trimHandler'
    > & {
      empty: boolean;
      loading: boolean;
      muteHandler: () => void;
      toggleTrimModalOpened: () => void;
    }) =>
    (!empty || removeHandler) && (
      <Flex
        sx={{
          ...headerStyles,
          bg: empty || loading ? 'muted3' : 'grey6',
          borderRadius: (theme: Theme) => `${theme.radii.default}px ${theme.radii.default}px 0 0`
        }}
      >
        <div>
          {!(empty || loading) && (
            <ScalingModeSelect onChange={scaleModeChangeHandler} value={scalingMode} />
          )}
        </div>

        <div>
          {type === SceneType.VIDEO && setMute && (
            <IconButton onClick={() => muteHandler()} sx={iconStyles} data-test="scene_mute-button">
              <Icon name={mute ? 'mute-editor' : 'unmute-editor'} type={IconTypes.White} />
            </IconButton>
          )}
          {!(empty || loading) && trimHandler && (
            <IconButton
              onClick={withStopPropagation(toggleTrimModalOpened)}
              sx={iconStyles}
              data-test="scene_trim-button"
            >
              <Icon name="trim-editor" type={IconTypes.White} />
            </IconButton>
          )}
          {removeHandler && (
            <IconButton
              onClick={withStopPropagation(removeHandler)}
              sx={iconStyles}
              data-test="scene_remove-button"
            >
              <Icon name="delete-editor" type={IconTypes.White} />
            </IconButton>
          )}
        </div>
      </Flex>
    )
);

const SceneCard: FC<SceneCardProps> = ({
  removeHandler,
  type,
  duration,
  maxDuration,
  active,
  selectHandler,
  trimHandler,
  trimStart,
  scalingMode,
  scaleModeChangeHandler,
  creationProgress,
  setMute,
  mute,
  downloadingProgress,
  isScenePaid,
  setPlayingHandler,
  isUploaded,
  localSource
}) => {
  const cachedSrc = localSource;
  const empty = type === SceneType.EMPTY;
  const loading = !empty && !cachedSrc;
  const [trimModalOpened, toggleTrimModalOpened] = useToggle(false);
  const previewRef = useRef(null);

  const muteHandler = useCallback(() => {
    setMute(!mute);
  }, [mute]);

  useEffect(() => {
    if (trimModalOpened) {
      setPlayingHandler(true);
      analytics.videoEditItemOpen(ToolName.Trim);
    }
  }, [trimModalOpened]);

  useEffect(() => {
    if (previewRef.current && type === SceneType.VIDEO) {
      previewRef.current.currentTime = trimStart / 1000;
    }
  }, [trimStart, previewRef.current]);

  return (
    <AspectRatio
      ratio={1.42}
      className="sceneCard-wrap"
      sx={{ overflow: 'hidden', borderRadius: '8px' }}
    >
      <div
        sx={{ height: '100%' }}
        data-test="scene"
        data-uploaded={isUploaded !== false && type !== SceneType.EMPTY}
      >
        <Header
          loading={loading}
          scalingMode={scalingMode}
          empty={empty}
          type={type}
          mute={mute}
          muteHandler={muteHandler}
          removeHandler={removeHandler}
          scaleModeChangeHandler={scaleModeChangeHandler}
          setMute={setMute}
          toggleTrimModalOpened={toggleTrimModalOpened}
          trimHandler={trimHandler}
        />
        {empty && (
          <Box sx={active ? emptyActiveSceneStyles : emptySceneStyles} onClick={selectHandler} />
        )}
        {loading && (
          <Flex sx={{ ...centeredContent, ...emptySceneStyles }}>
            <Progress
              sx={{ width: '80%', mb: 0, mt: 6, color: 'primary' }}
              max={100}
              value={creationProgress < 100 ? creationProgress : downloadingProgress}
            />
          </Flex>
        )}
        {cachedSrc && type === SceneType.IMAGE && (
          <Fragment>
            <Box sx={selectHandleStyle} onClick={selectHandler} />
            <img ref={previewRef} sx={previewStyles} src={cachedSrc} alt="preview" />
            <Icons iconType="photos-editor" isScenePaid={isScenePaid} />
            <Duration value={duration} />
            <ImageDurationModal
              opened={trimModalOpened}
              closeHandler={toggleTrimModalOpened}
              src={cachedSrc}
              applyHandler={trimHandler}
              trimStart={0}
              duration={duration}
              maxDuration={maxDuration}
            />
          </Fragment>
        )}
        {cachedSrc && type === SceneType.VIDEO && (
          <Fragment>
            <Box sx={selectHandleStyle} onClick={selectHandler} />
            <video ref={previewRef} sx={previewStyles} src={cachedSrc} />
            <Icons iconType="video-editor" isScenePaid={isScenePaid} />
            <Duration value={duration} />
            <VideoTrimModal
              src={cachedSrc}
              opened={trimModalOpened}
              closeHandler={toggleTrimModalOpened}
              applyHandler={trimHandler}
              trimStart={trimStart}
              duration={duration}
              maxDuration={maxDuration}
            />
          </Fragment>
        )}
      </div>
    </AspectRatio>
  );
};

export default SceneCard;
